import React, { useState, useEffect } from "react";
import { useConfig } from "../context/ConfigContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { GripVertical, Plus, Trash2, Save, Loader2, Anchor } from "lucide-react";
import { SEASON_MONTHS, MONTHS_IT } from "../lib/season";
import PushToggle from "../components/PushToggle";
import { toast } from "sonner";

export default function Setup() {
  const { config, save } = useConfig();
  const { canWrite, user } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [mp, setMp] = useState(null);
  const [mpSaving, setMpSaving] = useState(false);

  useEffect(() => { if (config) setForm(JSON.parse(JSON.stringify(config))); }, [config]);
  useEffect(() => { api.get("/marpol/settings").then((r) => setMp(r.data)).catch(() => {}); }, []);

  if (!form) return <div className="h-64 animate-pulse rounded-xl bg-slate-200" />;

  const set = (k, v) => setForm({ ...form, [k]: v });

  const setMp2 = (k, v) => setMp({ ...mp, [k]: v });
  const setFac = (i, patch) => setMp({ ...mp, default_disposal_facilities: mp.default_disposal_facilities.map((f, idx) => idx === i ? { ...f, ...patch } : f) });
  const addFac = () => setMp({ ...mp, default_disposal_facilities: [...(mp.default_disposal_facilities || []), { name: "", port_unlocode: "", contact_email: "" }] });
  const removeFac = (i) => setMp({ ...mp, default_disposal_facilities: mp.default_disposal_facilities.filter((_, idx) => idx !== i) });
  const saveMarpol = async () => {
    setMpSaving(true);
    try {
      const { data } = await api.put("/marpol/settings", {
        ship_name: mp.ship_name || "", imo_number: mp.imo_number || "", gt: Number(mp.gt) || 0,
        flag_state: mp.flag_state || "", max_persons_authorized: Number(mp.max_persons_authorized) || 0,
        responsible_officer: mp.responsible_officer || "", waste_manager: mp.waste_manager || "",
        incinerator_onboard: !!mp.incinerator_onboard, comminuter_onboard: !!mp.comminuter_onboard,
        default_disposal_facilities: mp.default_disposal_facilities || [],
      });
      setMp(data);
      toast.success("Impostazioni MARPOL salvate");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel salvataggio MARPOL"); }
    finally { setMpSaving(false); }
  };

  const setCat = (i, patch) => set("categories", form.categories.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  const addCat = () => set("categories", [...form.categories, { id: `c${Date.now()}`, name: "Nuova categoria", is_food: true, order: form.categories.length, color: "#64748B" }]);
  const removeCat = (i) => set("categories", form.categories.filter((_, idx) => idx !== i));

  const onDrop = (i) => {
    if (dragIdx === null || dragIdx === i) return;
    const arr = [...form.categories];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(i, 0, moved);
    set("categories", arr.map((c, idx) => ({ ...c, order: idx })));
    setDragIdx(null);
  };

  const doSave = async () => {
    setSaving(true);
    try {
      await save({
        name: form.name, color: form.color, logo_url: form.logo_url,
        season_start: form.season_start, season_end: form.season_end,
        standard_crew: Number(form.standard_crew), haccp_responsible: form.haccp_responsible,
        categories: form.categories, sandbox: form.sandbox, owner_email: form.owner_email,
        use_inventory: form.use_inventory,
        haccp_warn_days: Number(form.haccp_warn_days) || 7,
        haccp_alert_email: form.haccp_alert_email,
        monthly_budget: Number(form.monthly_budget) || 0,
        monthly_budgets: form.monthly_budgets || {},
        guest_budgets: form.guest_budgets || {},
        crew_budgets: form.crew_budgets || {},
      });
      toast.success("Impostazioni salvate");
    } catch (e) { toast.error("Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-head text-3xl font-bold tracking-tight text-navy">Impostazioni</h1>
          <p className="text-slate-500">Identità yacht, stagione, equipaggio e categorie</p>
        </div>
        {canWrite && (
          <button data-testid="save-setup-btn" onClick={doSave} disabled={saving} className="flex h-14 items-center gap-2 rounded-xl bg-gold px-6 font-bold text-black hover:bg-gold-hover disabled:opacity-60">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />} Salva
          </button>
        )}
      </div>

      {!canWrite && <div className="rounded-lg bg-slate-100 px-4 py-3 text-sm text-slate-600">Sei in sola lettura (Armatore).</div>}

      <Section title="Identità yacht">
        <div className="flex items-center gap-4">
          <img src={form.logo_url || "/logo.png"} alt="logo" className="h-16 w-16 rounded-xl border border-slate-200" />
          <div className="flex-1 space-y-3">
            <Field label="Nome yacht" value={form.name} onChange={(v) => set("name", v)} disabled={!canWrite} testid="setup-name" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-sm font-semibold text-navy">Colore tema</label>
                <input data-testid="setup-color" type="color" disabled={!canWrite} value={form.color} onChange={(e) => set("color", e.target.value)} className="h-12 w-full rounded-lg border-2 border-slate-200" />
              </div>
              <Field label="URL logo" value={form.logo_url} onChange={(v) => set("logo_url", v)} disabled={!canWrite} testid="setup-logo" />
            </div>
          </div>
        </div>
      </Section>

      <Section title="Stagione & equipaggio">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Inizio stagione" type="date" value={form.season_start} onChange={(v) => set("season_start", v)} disabled={!canWrite} testid="setup-season-start" />
          <Field label="Fine stagione" type="date" value={form.season_end} onChange={(v) => set("season_end", v)} disabled={!canWrite} testid="setup-season-end" />
          <Field label="Equipaggio standard" type="number" value={form.standard_crew} onChange={(v) => set("standard_crew", v)} disabled={!canWrite} testid="setup-crew" />
          <Field label="Responsabile HACCP" value={form.haccp_responsible} onChange={(v) => set("haccp_responsible", v)} disabled={!canWrite} testid="setup-haccp" />
        </div>
      </Section>

      <Section title="Budget Armatore" subtitle="Budget di spesa per ogni mese della stagione, concordato con l'Armatore. Mostrato in Dashboard e nel report.">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {SEASON_MONTHS.map((m) => {
            const key = `2026-${String(m).padStart(2, "0")}`;
            const val = (form.monthly_budgets && form.monthly_budgets[key] != null) ? form.monthly_budgets[key] : "";
            return (
              <div key={m}>
                <label className="mb-1 block text-sm font-semibold text-navy">{MONTHS_IT[m]}</label>
                <div className="flex items-center gap-1">
                  <span className="text-slate-400">€</span>
                  <input
                    data-testid={`setup-budget-${m}`}
                    type="number"
                    disabled={!canWrite}
                    value={val}
                    onChange={(e) => set("monthly_budgets", { ...(form.monthly_budgets || {}), [key]: e.target.value })}
                    className="tnum h-12 w-full rounded-lg border-2 border-slate-200 px-3 text-right outline-none focus:border-gold disabled:bg-slate-100"
                    placeholder="0"
                  />
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-4 border-t border-slate-100 pt-4">
          <Field label="Budget totale stagione (EUR) — opzionale" type="number" value={form.season_budget} onChange={(v) => set("season_budget", v)} disabled={!canWrite} testid="setup-season-budget" />
          <p className="mt-1 text-xs text-slate-400">Se vuoto, l'avanzamento stagionale usa la somma dei budget mensili.</p>
        </div>
      </Section>

      <Section title="Budget Ospiti/Clienti e Ciurma" subtitle="Budget separati concordati con l'Armatore. La spesa del mese viene ripartita automaticamente tra ospiti e ciurma in base ai coperti registrati.">
        <div className="mb-2 text-sm font-bold text-navy">Ospiti / Clienti</div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {SEASON_MONTHS.map((m) => {
            const key = `2026-${String(m).padStart(2, "0")}`;
            const val = (form.guest_budgets && form.guest_budgets[key] != null) ? form.guest_budgets[key] : "";
            return (
              <div key={`g${m}`}>
                <label className="mb-1 block text-sm font-semibold text-navy">{MONTHS_IT[m]}</label>
                <div className="flex items-center gap-1">
                  <span className="text-slate-400">€</span>
                  <input data-testid={`setup-guest-budget-${m}`} type="number" disabled={!canWrite} value={val}
                    onChange={(e) => set("guest_budgets", { ...(form.guest_budgets || {}), [key]: e.target.value })}
                    className="tnum h-12 w-full rounded-lg border-2 border-slate-200 px-3 text-right outline-none focus:border-gold disabled:bg-slate-100" placeholder="0" />
                </div>
              </div>
            );
          })}
        </div>
        <div className="mb-2 mt-5 text-sm font-bold text-navy">Ciurma</div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {SEASON_MONTHS.map((m) => {
            const key = `2026-${String(m).padStart(2, "0")}`;
            const val = (form.crew_budgets && form.crew_budgets[key] != null) ? form.crew_budgets[key] : "";
            return (
              <div key={`c${m}`}>
                <label className="mb-1 block text-sm font-semibold text-navy">{MONTHS_IT[m]}</label>
                <div className="flex items-center gap-1">
                  <span className="text-slate-400">€</span>
                  <input data-testid={`setup-crew-budget-${m}`} type="number" disabled={!canWrite} value={val}
                    onChange={(e) => set("crew_budgets", { ...(form.crew_budgets || {}), [key]: e.target.value })}
                    className="tnum h-12 w-full rounded-lg border-2 border-slate-200 px-3 text-right outline-none focus:border-gold disabled:bg-slate-100" placeholder="0" />
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      <Section title="Report Armatore">
        <Field label="Email Armatore (per invio PDF)" type="email" value={form.owner_email} onChange={(v) => set("owner_email", v)} disabled={!canWrite} testid="setup-owner-email" />
      </Section>

      <Section title="Scadenze HACCP" subtitle="Avvisi automatici sui prodotti in scadenza.">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Giorni di preavviso" type="number" value={form.haccp_warn_days} onChange={(v) => set("haccp_warn_days", v)} disabled={!canWrite} testid="setup-warn-days" />
          <Field label="Email avvisi (opz., altrimenti Armatore)" type="email" value={form.haccp_alert_email} onChange={(v) => set("haccp_alert_email", v)} disabled={!canWrite} testid="setup-haccp-email" />
        </div>
      </Section>

      <Section title="Notifiche push" subtitle="Ricevi gli avvisi di scadenza HACCP sul telefono anche ad app chiusa. Attiva su ogni dispositivo su cui vuoi riceverle.">
        <PushToggle />
      </Section>

      <Section title="Categorie di spesa" subtitle="Trascina per riordinare. Le categorie non-food sono escluse dal food cost.">
        <div className="space-y-2">
          {form.categories.map((c, i) => (
            <div
              key={c.id}
              data-testid={`cat-${i}`}
              draggable={canWrite}
              onDragStart={() => setDragIdx(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(i)}
              className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white p-2"
            >
              <GripVertical className="h-5 w-5 shrink-0 cursor-grab text-slate-300" />
              <input disabled={!canWrite} value={c.name} onChange={(e) => setCat(i, { name: e.target.value })} className="h-10 flex-1 rounded border border-slate-200 px-2 text-sm outline-none focus:border-gold" data-testid={`cat-name-${i}`} />
              <label className="flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-navy">
                <input disabled={!canWrite} type="checkbox" checked={c.is_food} onChange={(e) => setCat(i, { is_food: e.target.checked })} className="h-4 w-4 accent-gold" data-testid={`cat-food-${i}`} />
                Food
              </label>
              {canWrite && <button onClick={() => removeCat(i)} className="rounded p-1.5 text-red-400 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>}
            </div>
          ))}
        </div>
        {canWrite && <button data-testid="add-cat-btn" onClick={addCat} className="mt-3 flex items-center gap-1 rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium hover:bg-slate-200"><Plus className="h-4 w-4" /> Aggiungi categoria</button>}
      </Section>

      {mp && user?.role !== "stewardess" && (
        <Section title="MARPOL — Registro Rifiuti" subtitle="Identità nave, responsabili e impianti portuali usati nel Garbage Record Book e nei preavvisi (AWN).">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <span data-testid="mp-grb-badge" className={`rounded-full px-3 py-1 text-xs font-bold ${mp.grb_required ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
              {mp.grb_required ? "GRB obbligatorio" : "GRB non obbligatorio"}
            </span>
            <span className="text-xs text-slate-400">Calcolato: GT ≥ 400 oppure oltre 15 persone autorizzate</span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Nome nave" value={mp.ship_name} onChange={(v) => setMp2("ship_name", v)} disabled={!canWrite} testid="mp-ship-name" />
            <Field label="Numero IMO (7 cifre)" value={mp.imo_number} onChange={(v) => setMp2("imo_number", v)} disabled={!canWrite} testid="mp-imo" />
            <Field label="Stazza lorda (GT)" type="number" value={mp.gt} onChange={(v) => setMp2("gt", v)} disabled={!canWrite} testid="mp-gt" />
            <Field label="Stato di bandiera" value={mp.flag_state} onChange={(v) => setMp2("flag_state", v)} disabled={!canWrite} testid="mp-flag" />
            <Field label="Persone autorizzate a bordo" type="number" value={mp.max_persons_authorized} onChange={(v) => setMp2("max_persons_authorized", v)} disabled={!canWrite} testid="mp-persons" />
            <Field label="Ufficiale responsabile" value={mp.responsible_officer} onChange={(v) => setMp2("responsible_officer", v)} disabled={!canWrite} testid="mp-officer" />
            <Field label="Responsabile rifiuti" value={mp.waste_manager} onChange={(v) => setMp2("waste_manager", v)} disabled={!canWrite} testid="mp-manager" />
          </div>
          <div className="mt-4 flex flex-wrap gap-5">
            <label className="flex items-center gap-2 text-navy"><input type="checkbox" disabled={!canWrite} checked={!!mp.incinerator_onboard} onChange={(e) => setMp2("incinerator_onboard", e.target.checked)} className="h-5 w-5 accent-gold" data-testid="mp-incinerator" /> <span className="font-medium">Inceneritore a bordo</span></label>
            <label className="flex items-center gap-2 text-navy"><input type="checkbox" disabled={!canWrite} checked={!!mp.comminuter_onboard} onChange={(e) => setMp2("comminuter_onboard", e.target.checked)} className="h-5 w-5 accent-gold" data-testid="mp-comminuter" /> <span className="font-medium">Trituratore (comminuter) a bordo</span></label>
          </div>
          <div className="mt-5">
            <div className="mb-2 text-sm font-bold text-navy">Impianti portuali di conferimento</div>
            <div className="space-y-2">
              {(mp.default_disposal_facilities || []).map((f, i) => (
                <div key={i} data-testid={`mp-fac-${i}`} className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white p-2">
                  <input disabled={!canWrite} value={f.name || ""} onChange={(e) => setFac(i, { name: e.target.value })} placeholder="Nome impianto" className="h-10 min-w-[160px] flex-1 rounded border border-slate-200 px-2 text-sm outline-none focus:border-gold" />
                  <input disabled={!canWrite} value={f.port_unlocode || ""} onChange={(e) => setFac(i, { port_unlocode: e.target.value })} placeholder="UN/LOCODE" className="h-10 w-28 rounded border border-slate-200 px-2 text-sm outline-none focus:border-gold" />
                  <input disabled={!canWrite} value={f.contact_email || ""} onChange={(e) => setFac(i, { contact_email: e.target.value })} placeholder="Email/PEC" className="h-10 min-w-[180px] flex-1 rounded border border-slate-200 px-2 text-sm outline-none focus:border-gold" />
                  {canWrite && <button onClick={() => removeFac(i)} className="rounded p-1.5 text-red-400 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>}
                </div>
              ))}
            </div>
            {canWrite && <button data-testid="mp-add-fac" onClick={addFac} className="mt-3 flex items-center gap-1 rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium hover:bg-slate-200"><Plus className="h-4 w-4" /> Aggiungi impianto</button>}
          </div>
          {canWrite && (
            <button data-testid="mp-save-btn" onClick={saveMarpol} disabled={mpSaving} className="mt-5 flex h-12 items-center gap-2 rounded-xl bg-navy px-5 font-bold text-white hover:bg-navy-hover disabled:opacity-60">
              {mpSaving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />} Salva MARPOL
            </button>
          )}
        </Section>
      )}

      <Section title="Avanzate">
        <label className="flex cursor-pointer items-center gap-3 text-navy">
          <input data-testid="setup-sandbox" type="checkbox" disabled={!canWrite} checked={form.sandbox} onChange={(e) => set("sandbox", e.target.checked)} className="h-5 w-5 accent-gold" />
          <span className="font-medium">Modalità sandbox <span className="text-sm text-slate-400">(per test — evidenzia i dati come non reali)</span></span>
        </label>
        <label className="mt-3 flex cursor-pointer items-center gap-3 text-navy">
          <input data-testid="setup-use-inventory" type="checkbox" disabled={!canWrite} checked={form.use_inventory} onChange={(e) => set("use_inventory", e.target.checked)} className="h-5 w-5 accent-gold" />
          <span className="font-medium">Usa giacenze mensili nel food cost</span>
        </label>
      </Section>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="mb-4 flex items-center gap-2">
        <Anchor className="h-4 w-4 text-gold" />
        <h3 className="font-head text-lg font-semibold text-navy">{title}</h3>
      </div>
      {subtitle && <p className="mb-3 -mt-2 text-sm text-slate-400">{subtitle}</p>}
      {children}
    </div>
  );
}
function Field({ label, value, onChange, type = "text", disabled, testid }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-semibold text-navy">{label}</label>
      <input data-testid={testid} type={type} disabled={disabled} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-12 w-full rounded-lg border-2 border-slate-200 px-3 outline-none focus:border-gold disabled:bg-slate-100" />
    </div>
  );
}
